/**
 * The device signing key (decision 55; part 25a log, the key list and table D): an ECDSA P-256 key pair made by WebCrypto
 * at login, whose private key can't be exported, so a copy of the browser's storage can't sign as the device elsewhere.
 * Its public key is the device's ID at the relay, which gets session tokens by having it sign a challenge (part 29).
 * P-256, not Ed25519: every engine the app supports has it (the user's choice, 2026-10-21).
 */

/** The signing algorithm: ECDSA on P-256, signatures over SHA-256. */
export const DEVICE_SIGNING_KEY = { name: 'ECDSA', namedCurve: 'P-256' } as const;

/** The signature parameters to sign and verify with. */
export const DEVICE_SIGNATURE = { name: 'ECDSA', hash: 'SHA-256' } as const;

/** A device's signing key: the pair, and the public key as SPKI bytes, which the relay is sent. */
export interface DeviceSigningKey {
  readonly privateKey: CryptoKey;
  readonly publicKey: CryptoKey;
  readonly publicKeySpki: Uint8Array<ArrayBuffer>;
}

/**
 * D1–D4: a new signing key pair, the private key non-extractable and for signing only, the public key for verifying
 * (WebCrypto always lets a public key be exported).
 */
export async function createDeviceSigningKey(subtle: SubtleCrypto): Promise<DeviceSigningKey> {
  const pair = await subtle.generateKey(DEVICE_SIGNING_KEY, false, ['sign', 'verify']);
  const spki = await subtle.exportKey('spki', pair.publicKey);
  return { privateKey: pair.privateKey, publicKey: pair.publicKey, publicKeySpki: new Uint8Array(spki) };
}
